import './Login.css';
import React, { useContext, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { createSession } from '../../model/session';
import { Button } from '../../components/Button/Button';
import { DBInspector } from '../../components/DBInspector/DBInspector';
import { publicPath } from '../../model/config';
import { useForm } from '@nerdgeschoss/react-use-form-library';
import { FormTextField } from '../../components/Form/TextField/FormTextField';
import { FormCheckbox } from '../../components/Form/Checkbox/FormCheckbox';
import { handleError } from '../../model/notification';
import { I18n, t } from '../../locales/translate';
import { Stack } from '../../components/Stack/Stack';
import { useMeta } from '../../model/meta';
import { signUpPath } from '../../routes';
import { __RouterContext } from 'react-router';
import { Gatekeeper } from '../Gatekeeper/Gatekeeper';
import { Collapse } from '@nerdgeschoss/shimmer-component-collapse';

interface LoginModel {
  email?: string | null;
  password?: string | null;
  rememberLogin?: boolean;
}

export function Login(): JSX.Element {
  const { history } = useContext(__RouterContext);

  const [displayCredentialsError, setDisplayCredentialsError] = useState(false);
  const { model, fields, onSubmit, valid, submissionStatus } = useForm<LoginModel>({
    model: {
      rememberLogin: true,
    },
    onSubmit: async () => {
      setDisplayCredentialsError(false);
      await createSession({
        language: I18n.locale,
        email: model.email,
        password: model.password,
        rememberLogin: model.rememberLogin,
      });
    },
    onSubmitError: (error) => {
      if ((error as Error & { response?: Response }).response?.status === 401) {
        setDisplayCredentialsError(true);
      } else {
        handleError(error);
      }
    },
    validations: {
      email: ['required', 'email'],
      password: 'required',
    },
  });
  useMeta({ title: t.page.login.loginFormTitle, themeColor: '#ededed' });

  const path = history.location.pathname;
  const token = path.includes('invites') ? path.split('invites/')[1] : null;

  return (
    <Gatekeeper>
      <Collapse open={displayCredentialsError}>
        <div className="login-form__invalid">
          <span>{t.errors.invalidUserMsg}</span>
        </div>
      </Collapse>
      <form autoComplete="on">
        <div className="login-form__content">
          <Stack size="sm">
            <Stack size="xxs">
              <h1>{t.page.login.loginFormTitle}</h1>
              {token && <small>{t.page.login.hasAccount}</small>}
            </Stack>
            <FormTextField
              name="email"
              label={t.model.user.email}
              type="email"
              icon="at-sign--sm"
              autoFocus={true}
              inputId="email"
              {...fields.email}
            />
            <Stack size="xxs">
              <FormTextField
                inputId="password"
                name="password"
                label={t.page.login.password}
                icon="lock--sm"
                type="password"
                {...fields.password}
              />
              <div className="login-form__forgot-password">
                <NavLink to={`${publicPath}/password-reset`}>{`${t.page.login.forgotPassword}?`}</NavLink>
              </div>
            </Stack>
            <FormCheckbox label={`${t.page.login.rememberLogin}?`} {...fields.rememberLogin} />
            <div />
            <Stack size="xs">
              <Button
                type="primary"
                isSubmit
                onClick={onSubmit}
                block
                title={t.page.login.title}
                loading={submissionStatus === 'submitting'}
                disabled={!valid}
              />
              {token && (
                <NavLink to={signUpPath(token)}>
                  <Button type="outline" block title={t.page.login.createAccount} />
                </NavLink>
              )}
            </Stack>
          </Stack>
        </div>
      </form>
      <DBInspector />
    </Gatekeeper>
  );
}
